/**
 * ProfileGate — intercepts any authenticated user who has no email on file
 * and shows a blocking modal requiring them to provide one before proceeding.
 *
 * Features:
 * - Welcoming entrance animation (fade + slide up)
 * - Inline real-time email format validation (debounced, shows on blur or after typing)
 * - Visual feedback: green check when email is valid, red border + message when invalid
 * - Name field with character counter
 * - Smooth success state before closing
 */
import { useState, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

// ── Email validation ──────────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validateEmail(value: string): string | null {
  if (!value.trim()) return "Email is required.";
  if (!EMAIL_RE.test(value.trim())) return "Please enter a valid email address.";
  return null;
}

// ── Animated icon header ──────────────────────────────────────────────────────
function WelcomeHeader() {
  return (
    <div className="flex flex-col items-center gap-3 pb-2" style={{ animation: "pgFadeSlide 0.5s cubic-bezier(0.34,1.4,0.64,1) both" }}>
      <div className="relative flex items-center justify-center">
        {/* Outer pulse ring */}
        <span
          className="absolute inline-flex size-16 rounded-full bg-primary/10"
          style={{ animation: "pgPulse 2.4s ease-in-out infinite" }}
        />
        {/* Icon circle */}
        <span className="relative flex items-center justify-center size-14 rounded-full bg-primary/15">
          <Sparkles className="size-7 text-primary" />
        </span>
      </div>
      <div className="text-center space-y-0.5">
        <DialogTitle className="text-xl font-semibold tracking-tight">
          Welcome to Lola &amp; Ber
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Just one quick step before you dive in.
        </DialogDescription>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ProfileGate({ children }: { children: React.ReactNode }) {
  const { user, loading, refresh } = useAuth();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Debounce inline validation — only show errors after user pauses typing
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleEmailChange(value: string) {
    setEmail(value);
    setServerError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (emailTouched) {
      debounceRef.current = setTimeout(() => {
        setEmailError(validateEmail(value));
      }, 350);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setEmailError(validateEmail(email));
  }

  // Cleanup debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const completeProfile = trpc.auth.completeProfile.useMutation({
    onSuccess: async () => {
      setSuccess(true);
      // Brief success pause so the user sees the checkmark before the gate closes
      await new Promise((r) => setTimeout(r, 900));
      await refresh?.();
    },
    onError: (err) => {
      setServerError(err.message ?? "Something went wrong. Please try again.");
    },
  });

  // While auth is loading, render nothing (avoids flash)
  if (loading) return null;

  // If user is not authenticated, render children as-is (login page handles it)
  if (!user) return <>{children}</>;

  // If user already has an email, gate is satisfied — render children normally
  if (user.email) return <>{children}</>;

  // User is authenticated but has no email — show blocking modal
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setEmailTouched(true);
    const err = validateEmail(email);
    setEmailError(err);
    if (err) return;
    completeProfile.mutate({
      email: email.trim().toLowerCase(),
      name: name.trim() || undefined,
    });
  };

  const isEmailValid = emailTouched && !emailError && EMAIL_RE.test(email.trim());
  const isEmailInvalid = emailTouched && !!emailError;

  return (
    <>
      {/* Render children behind the modal so the layout is already mounted */}
      {children}

      {/* Keyframe styles injected inline */}
      <style>{`
        @keyframes pgFadeSlide {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes pgPulse {
          0%, 100% { transform: scale(1);    opacity: 0.6; }
          50%       { transform: scale(1.18); opacity: 0.2; }
        }
        @keyframes pgSuccessBounce {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>

      <Dialog open modal>
        <DialogContent
          className="sm:max-w-md overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {success ? (
            /* ── Success state ── */
            <div
              className="flex flex-col items-center gap-4 py-8 text-center"
              style={{ animation: "pgFadeSlide 0.4s ease-out both" }}
            >
              <CheckCircle2
                className="size-16 text-primary"
                style={{ animation: "pgSuccessBounce 0.5s cubic-bezier(0.34,1.56,0.64,1) both" }}
              />
              <div>
                <p className="text-lg font-semibold">You're all set!</p>
                <p className="text-sm text-muted-foreground mt-1">Taking you in…</p>
              </div>
            </div>
          ) : (
            /* ── Form state ── */
            <div style={{ animation: "pgFadeSlide 0.45s cubic-bezier(0.34,1.2,0.64,1) both" }}>
              <DialogHeader className="mb-4">
                <WelcomeHeader />
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email field */}
                <div className="space-y-1.5">
                  <Label htmlFor="profile-email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="profile-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      required
                      autoFocus
                      className={[
                        "pr-9 transition-colors",
                        isEmailInvalid ? "border-destructive focus-visible:ring-destructive/30" : "",
                        isEmailValid  ? "border-primary   focus-visible:ring-primary/30"     : "",
                      ].join(" ")}
                      aria-invalid={isEmailInvalid}
                      aria-describedby={isEmailInvalid ? "email-error" : undefined}
                    />
                    {/* Inline status icon */}
                    {isEmailValid && (
                      <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-primary pointer-events-none" />
                    )}
                    {isEmailInvalid && (
                      <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-destructive pointer-events-none" />
                    )}
                  </div>
                  {isEmailInvalid && (
                    <p id="email-error" className="text-xs text-destructive flex items-center gap-1 mt-1" role="alert">
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Name field */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="profile-name">
                      Name <span className="text-muted-foreground text-xs">(optional)</span>
                    </Label>
                    {name.length > 0 && (
                      <span className={`text-xs tabular-nums ${name.length > 100 ? "text-destructive" : "text-muted-foreground"}`}>
                        {name.length}/128
                      </span>
                    )}
                  </div>
                  <Input
                    id="profile-name"
                    type="text"
                    placeholder="Your name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 128))}
                    maxLength={128}
                  />
                </div>

                {/* Server error */}
                {serverError && (
                  <p className="text-sm text-destructive flex items-center gap-1.5" role="alert">
                    <AlertCircle className="size-3.5 shrink-0" />
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={completeProfile.isPending || isEmailInvalid}
                >
                  {completeProfile.isPending ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Continue →"
                  )}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  Your information is only used to personalise your experience.
                </p>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
